import { useApi } from '~/composables/useApi'
import { useAuthModal } from '~/composables/useAuthModal'
import { useDiscoveryLocation } from '~/composables/useDiscoveryLocation'
import { useInflight } from '~/composables/useInflight'
import { useKawadernaI18n } from '~/composables/useKawadernaI18n'
import { useProfileAccount } from '~/composables/useProfileAccount'
import { requestHostname, shouldSecureAuthCookie } from '~/utils/authCookie'
import { beginSessionReauth, finishSessionReauth } from '~/utils/sessionReauth'

export type AccountType = 'seeker' | 'company' | 'hiring_agent'
/** Signup interest wizard: store, jobs, or shopping. */
export type PrimaryInterest = 'store' | 'jobs' | 'shops'

export interface AuthUser {
  id: number
  phone: string
  email: string | null
  username: string | null
  /** Human-readable display name from the API (full name / company / username). */
  name: string | null
  accountType: AccountType
  /** Last switcher persona persisted on the user row. */
  lastAccount: AccountType
  /** Null for legacy accounts that signed up before interest was persisted. */
  primaryInterest: PrimaryInterest | null
  /** Dial digits chosen with the phone country, such as 20 for Egypt. */
  phoneCountryCode: string | null
  mustChangePassword?: boolean
  isSuperAdmin: boolean
  createdAt: string
}

interface ApiUser {
  id: number
  phone: string
  email?: string | null
  username?: string | null
  name?: string | null
  account_type?: string | null
  last_account?: string | null
  primary_interest?: string | null
  phone_country_code?: string | null
  must_change_password?: boolean
  is_super_admin?: boolean
  created_at: string
}

const USER_CACHE_KEY = 'kawaderna_user'

function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, '')
}

function normalizePrimaryInterest(value: unknown): PrimaryInterest | null {
  return value === 'store' || value === 'jobs' || value === 'shops' ? value : null
}

function normalizeAccountType(value: unknown): AccountType {
  return value === 'company' || value === 'hiring_agent' ? value : 'seeker'
}

function toAuthUser(user: ApiUser): AuthUser {
  const accountType = normalizeAccountType(user.account_type)
  const name = (user.name ?? user.username ?? null)?.trim() || null
  return {
    id: user.id,
    phone: user.phone,
    email: user.email ?? null,
    username: user.username ?? null,
    name,
    accountType,
    lastAccount: normalizeAccountType(user.last_account ?? user.account_type),
    primaryInterest: normalizePrimaryInterest(user.primary_interest),
    phoneCountryCode: String(user.phone_country_code || '').replace(/\D/g, '') || null,
    mustChangePassword: !!user.must_change_password,
    isSuperAdmin: !!user.is_super_admin,
    createdAt: user.created_at,
  }
}

function parseCachedUser(raw: string | null | undefined): AuthUser | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as AuthUser
    if (!parsed || typeof parsed.id !== 'number') return null
    return {
      id: parsed.id,
      phone: parsed.phone ?? '',
      email: parsed.email ?? null,
      username: parsed.username ?? null,
      name: parsed.name ?? parsed.username ?? null,
      accountType: normalizeAccountType(parsed.accountType),
      lastAccount: normalizeAccountType(parsed.lastAccount ?? parsed.accountType),
      primaryInterest: normalizePrimaryInterest(parsed.primaryInterest),
      phoneCountryCode: String(parsed.phoneCountryCode || '').replace(/\D/g, '') || null,
      mustChangePassword: !!parsed.mustChangePassword,
      isSuperAdmin: !!parsed.isSuperAdmin,
      createdAt: parsed.createdAt ?? '',
    }
  }
  catch {
    return null
  }
}

function safeGetCachedUserFromStorage(): AuthUser | null {
  if (!import.meta.client) return null
  try {
    return parseCachedUser(localStorage.getItem(USER_CACHE_KEY))
  }
  catch {
    return null
  }
}

function safeSetCachedUserStorage(user: AuthUser | null): void {
  if (!import.meta.client) return
  try {
    if (!user) localStorage.removeItem(USER_CACHE_KEY)
    else localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user))
  }
  catch {
    // Best effort only.
  }
}

/**
 * Phone + password authentication backed by the Laravel API (Sanctum
 * tokens). The public surface is unchanged from the original localStorage
 * implementation so pages and middleware keep working; only the source of
 * truth moved to the backend.
 */
export function useAuth() {
  const { apiFetch, setToken, clearToken, loadToken, token } = useApi()
  const { t } = useKawadernaI18n()
  const config = useRuntimeConfig()
  // Bound during setup so signOut/authenticate can reset after awaits.
  const discoveryLocation = useDiscoveryLocation()
  const user = useState<AuthUser | null>('auth-user', () => null)
  const ready = useState('auth-ready', () => false)
  const verified = useState('auth-session-verified', () => false)
  const verifyInflight = useInflight<void>('auth-verify-session')
  // Cookie mirror so SSR can hydrate the user before /auth/me returns —
  // otherwise a logged-in visitor briefly (or fully) sees the auth landing page.
  const cookieDomain = String(config.public.cookieDomain || '').trim()
  const userCookie = useSharedCookie<string | null>(USER_CACHE_KEY, {
    maxAge: 60 * 60 * 24 * 365 * 10,
    sameSite: 'lax',
    path: '/',
    ...(cookieDomain ? { domain: cookieDomain } : {}),
    ...(shouldSecureAuthCookie({
      dev: import.meta.dev,
      hostname: requestHostname(),
      cookieDomain,
      siteUrl: String(config.public.siteUrl || ''),
    }) ? { secure: true } : {}),
  })

  const isAuthenticated = computed(() => !!user.value)
  const isSuperAdmin = computed(() => !!user.value?.isSuperAdmin)
  /** Token present in memory or the first-party session cookie. */
  const hasSessionToken = computed(() => {
    loadToken()
    return !!token.value
  })

  /** Prefer API display name, then username — never the phone number. */
  const displayName = computed(() => {
    const u = user.value
    if (!u) return ''
    return (u.name || u.username || '').trim()
  })

  function readCachedUser(): AuthUser | null {
    return parseCachedUser(userCookie.value) ?? safeGetCachedUserFromStorage()
  }

  function writeCachedUser(next: AuthUser | null): void {
    // Never mutate auth cookies during SSR — rewriting Set-Cookie against the
    // request cookies is a common source of Vue hydration mismatches.
    safeSetCachedUserStorage(next)
    if (!import.meta.client) return
    userCookie.value = next ? JSON.stringify(next) : null
  }

  async function fetchMe(): Promise<AuthUser> {
    // Retry transient network failures (e.g. a slow SSR fetch) so a momentary
    // blip never tears down a valid session.
    const res = await apiFetch<{ data: ApiUser }>('/auth/me', { retries: 2 })
    return toAuthUser(res.data)
  }

  function commitUser(next: AuthUser): AuthUser {
    user.value = next
    writeCachedUser(next)
    return next
  }

  /**
   * Confirm the token with /auth/me. Never blocks the auth→home redirect when a
   * cached user already exists (that wait was flashing the login page for seconds).
   *
   * SSR must not destroy a cached login: the server often cannot see the
   * bearer (or /auth/me 401s without it), and that used to wipe the user on
   * every refresh. A client 401 drops the bad token and opens the sign-in
   * popup on the current page. Network errors and 5xx leave the session.
   */
  async function verifySession(): Promise<void> {
    if (verified.value) return
    const bearer = loadToken()
    if (!bearer) return
    if (import.meta.server) return
    await verifyInflight.run(async () => {
      if (verified.value) return
      if (!loadToken()) return
      try {
        commitUser(await fetchMe())
        verified.value = true
      }
      catch (error) {
        // Only a rejected token (401) asks for sign-in. Network errors,
        // timeouts, and 5xx must NEVER delete a valid token or open login.
        // Keep the cached user so the route guard does not send this page to
        // the login screen; the blocking popup sits on top until they sign in.
        if (error instanceof ApiError && error.status === 401 && loadToken()) {
          clearToken()
          verified.value = false
          const { alreadyWaiting } = beginSessionReauth()
          if (!alreadyWaiting && import.meta.client) {
            try {
              useState('auth-session-invalidated', () => false).value = true
              useAuthModal().openAuth('signin', { blocking: true })
            }
            catch {
              // The popup is also opened by the request that received the 401.
            }
          }
        }
      }
    })
  }

  /**
   * Restore the session from a stored token.
   *
   * Fast path: hydrate cookie/localStorage user, mark ready, return immediately
   * so middleware can redirect — then re-verify /auth/me in the background.
   * Slow path (token, no cache): await /auth/me while the auth page shows a boot shell.
   */
  async function loadSession(): Promise<void> {
    const bearer = loadToken()
    const cached = readCachedUser()

    // Allow one client-side retry when SSR had a token but could not resolve the user.
    if (ready.value) {
      if (import.meta.client && !user.value) {
        if (cached) {
          user.value = cached
          if (bearer) void verifySession()
          return
        }
        if (bearer) {
          await verifySession()
          return
        }
      }
      // Keep the user cookie mirrored so the next SSR hit can redirect instantly.
      if (import.meta.client && user.value) writeCachedUser(user.value)
      return
    }

    if (cached) {
      user.value = cached
      ready.value = true
      writeCachedUser(cached)
      if (import.meta.client && bearer) void verifySession()
      return
    }

    if (!bearer) {
      // /auth/me is behind auth:sanctum. Calling it with no token always 401s
      // and is how guests (and localhost after a dropped Secure cookie) spam
      // the console. Do not wipe a cookie/localStorage user during SSR — the
      // client still has to read those stores after hydrate.
      if (import.meta.client) {
        user.value = null
        writeCachedUser(null)
      }
      ready.value = true
      return
    }

    // Owner chrome (tab pencils, add-tab) is `v-if="isOwner"`. Without the user
    // on the server those nodes are missing from the first HTML and pop in
    // after /auth/me. Resolve the user here. Never clear the token on SSR —
    // a 401 must not log the visitor out before the client can retry.
    if (import.meta.server) {
      try {
        commitUser(await fetchMe())
        verified.value = true
      }
      catch {
        // Keep the bearer.
      }
      ready.value = true
      return
    }

    await verifySession()
    ready.value = true
  }

  async function signUp(
    fullName: string,
    email: string,
    phone: string,
    password: string,
    confirmPassword: string,
    accountType: AccountType = 'seeker',
    sex?: 'male' | 'female' | null,
    dob?: string | null,
    location?: {
      country_id?: number | null
      state_id?: number | null
      city_id?: number | null
      city_text?: string | null
    } | null,
    phoneCountryCode?: string | null,
  ): Promise<AuthUser> {
    if (!fullName.trim()) {
      throw new Error('nameRequired')
    }
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      throw new Error('emailRequired')
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error('invalidEmail')
    }
    if (normalizePhone(phone).length < 8) {
      throw new Error('invalidPhone')
    }
    if (password.length < 8) {
      throw new Error('weakPassword')
    }
    if (password !== confirmPassword) {
      throw new Error('passwordMismatch')
    }

    try {
      const res = await apiFetch<{ data: { user: ApiUser, token: string } }>('/auth/register', {
        method: 'POST',
        body: {
          full_name: fullName.trim(),
          email: normalizedEmail,
          phone,
          ...(phoneCountryCode ? { phone_country_code: phoneCountryCode } : {}),
          password,
          password_confirmation: confirmPassword,
          account_type: accountType,
          ...(sex ? { sex } : {}),
          ...(dob ? { dob } : {}),
          ...(location?.country_id ? { country_id: location.country_id } : {}),
          ...(location?.state_id ? { state_id: location.state_id } : {}),
          ...(location?.city_id ? { city_id: location.city_id } : {}),
          ...(location?.city_text ? { city_text: location.city_text } : {}),
        },
      })
      return authenticate(res.data)
    }
    catch (error) {
      throw mapAuthError(error, t)
    }
  }

  async function signIn(phone: string, password: string): Promise<AuthUser> {
    // Accept either a phone number or an admin username; the backend resolves
    // which one it is, so only require a non-empty identifier here.
    if (!phone.trim() || !password) {
      throw new Error('invalidCredentials')
    }

    try {
      const res = await apiFetch<{ data: { user: ApiUser, token: string } }>('/auth/login', {
        method: 'POST',
        body: { phone, password },
      })
      return authenticate(res.data)
    }
    catch (error) {
      throw mapAuthError(error, t)
    }
  }

  async function requestPasswordReset(phone: string): Promise<void> {
    if (!phone.trim()) throw new Error('invalidPhone')
    try {
      await apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: { phone: phone.trim() },
      })
    }
    catch (error) {
      if (error instanceof ApiError && error.status === 429) throw new Error('forgotTooMany')
      throw mapAuthError(error, t)
    }
  }

  async function resetPassword(
    phone: string,
    code: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<void> {
    if (password.length < 8) throw new Error('forgotPasswordShort')
    if (password !== passwordConfirmation) throw new Error('passwordMismatch')
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: {
          phone: phone.trim(),
          code: code.trim(),
          password,
          password_confirmation: passwordConfirmation,
        },
      })
    }
    catch (error) {
      if (error instanceof ApiError && error.status === 429) throw new Error('forgotTooMany')
      if (error instanceof ApiError && error.code === 'invalidResetCode') throw new Error('invalidResetCode')
      throw mapAuthError(error, t)
    }
  }

  async function signOut(): Promise<void> {
    try {
      await apiFetch('/auth/logout', { method: 'POST' })
    }
    catch {
      // Best effort: clear the local session regardless of the response.
    }
    finally {
      clearToken()
      user.value = null
      writeCachedUser(null)
      verified.value = false
      discoveryLocation.reset()
      finishSessionReauth(false)
      try {
        useState('auth-session-invalidated', () => false).value = false
        useAuthModal().releaseAuthModal()
      }
      catch {
        // Sign-out still clears the token if this runs outside setup.
      }
    }
  }

  /**
   * Permanently deletes the current account and all of its data server-side,
   * then clears the local session. Rethrows on failure so the caller can keep
   * the user signed in and surface an error.
   */
  async function deleteAccount(password: string): Promise<void> {
    await apiFetch('/auth/account', {
      method: 'DELETE',
      body: { password },
    })
    clearToken()
    user.value = null
    writeCachedUser(null)
    verified.value = false
    discoveryLocation.reset()
    finishSessionReauth(false)
    try {
      useState('auth-session-invalidated', () => false).value = false
      useAuthModal().releaseAuthModal()
    }
    catch {
      // Account is already deleted; the local token is cleared above.
    }
  }

  async function changePassword(
    currentPassword: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<void> {
    if (password.length < 8) {
      throw new Error('passwordTooShort')
    }
    if (password !== passwordConfirmation) {
      throw new Error('passwordMismatch')
    }
    if (password === currentPassword) {
      throw new Error('passwordUnchanged')
    }
    try {
      const res = await apiFetch<{ data: { user?: ApiUser, token?: string, message?: string } }>('/auth/password', {
        method: 'POST',
        body: {
          current_password: currentPassword,
          password,
          password_confirmation: passwordConfirmation,
        },
      })
      if (res.data?.token) {
        setToken(res.data.token)
      }
      if (res.data?.user) {
        commitUser(toAuthUser(res.data.user))
      }
      else if (user.value?.mustChangePassword) {
        commitUser({ ...user.value, mustChangePassword: false })
      }
    }
    catch (error) {
      throw mapAuthError(error, t)
    }
  }

  function authenticate(payload: { user: ApiUser, token?: string }): AuthUser {
    // New session — drop any previous account's discovery pin.
    discoveryLocation.reset()
    if (payload.token) {
      setToken(payload.token)
    }
    ready.value = true
    verified.value = true
    const next = commitUser(toAuthUser(payload.user))
    // Switcher session comes from users.last_account on the login payload.
    restoreProfileAccount(next)
    // New token is already stored. Resume any request paused on the popup.
    finishSessionReauth(true)
    try {
      useState('auth-session-invalidated', () => false).value = false
    }
    catch {
      // Login can finish outside a component; the token is already saved.
    }
    return next
  }

  /** Set session after a flow that already returned a token (e.g. public apply). */
  function establishSession(token: string, apiUser?: ApiUser): void {
    discoveryLocation.reset()
    setToken(token)
    if (apiUser) {
      const next = commitUser(toAuthUser(apiUser))
      restoreProfileAccount(next)
      verified.value = true
    }
    ready.value = true
    finishSessionReauth(true)
    try {
      useState('auth-session-invalidated', () => false).value = false
    }
    catch {
      // Same as authenticate: the token is already stored.
    }
  }

  /** Restore the switcher from the DB last_account on the signed-in user. */
  function restoreProfileAccount(next: AuthUser): void {
    const { setAccount } = useProfileAccount()
    setAccount(next.lastAccount, { persist: false })
  }

  /** Persist the last switcher persona on the user row (optimistic + API). */
  async function persistLastAccount(account: AccountType): Promise<void> {
    if (!user.value) return
    if (user.value.lastAccount !== account) {
      commitUser({ ...user.value, lastAccount: account })
    }
    try {
      const res = await apiFetch<{ data: ApiUser }>('/auth/last-account', {
        method: 'PATCH',
        body: { last_account: account },
      })
      commitUser(toAuthUser(res.data))
    }
    catch {
      // Keep the optimistic value; the next successful persist or login wins.
    }
  }

  /** Align the profile-account switcher with a newly chosen account type. */
  function syncProfileAccount(accountType: AccountType): void {
    const { setAccount } = useProfileAccount()
    setAccount(
      accountType === 'company' || accountType === 'hiring_agent'
        ? accountType
        : 'seeker',
      { persist: false },
    )
  }

  /**
   * Post-signup interest wizard — persist seeker / company / hiring_agent
   * (and optional store vs jobs interest) and refresh the local session.
   */
  async function setAccountType(
    accountType: AccountType,
    primaryInterest?: PrimaryInterest | null,
  ): Promise<AuthUser> {
    const body: { account_type: AccountType, primary_interest?: PrimaryInterest } = {
      account_type: accountType,
    }
    if (primaryInterest === 'store' || primaryInterest === 'jobs' || primaryInterest === 'shops') {
      body.primary_interest = primaryInterest
    }
    const res = await apiFetch<{ data: ApiUser }>('/auth/account-type', {
      method: 'PATCH',
      body,
    })
    const next = commitUser(toAuthUser(res.data))
    syncProfileAccount(next.accountType)
    return next
  }

  /** Update the cached display name after the user edits their profile. */
  function setDisplayName(name: string | null): void {
    if (!user.value) return
    const next = { ...user.value, name: name?.trim() || null }
    commitUser(next)
  }

  function formatPhoneDisplay(phone: string): string {
    if (!phone) return ''
    if (phone.startsWith('20') && phone.length >= 11) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`
    }
    return `+${phone}`
  }

  return {
    user,
    ready,
    isAuthenticated,
    isSuperAdmin,
    hasSessionToken,
    displayName,
    signUp,
    signIn,
    requestPasswordReset,
    resetPassword,
    signOut,
    deleteAccount,
    changePassword,
    setAccountType,
    persistLastAccount,
    loadSession,
    verifySession,
    establishSession,
    setDisplayName,
    formatPhoneDisplay,
  }
}

/**
 * Translate an API failure into the error-code strings the auth page maps to
 * localized copy. Falls back to a generic message.
 */
function mapAuthError(
  error: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
): Error {
  if (error instanceof ApiError) {
    if (error.code === 'phoneExists' || error.code === 'emailExists' || error.code === 'invalidCredentials' || error.code === 'accountNotFound' || error.code === 'invalidPhone' || error.code === 'invalidResetCode') {
      return new Error(error.code)
    }
    if (error.status >= 500 || error.code === 'serverError') {
      return new Error('generic')
    }
    return new Error(formatApiError(error, t))
  }
  if (error instanceof Error && error.message) {
    return error
  }
  return new Error('generic')
}
